import {
  ActivatedRouteSnapshot,
  CanActivateChildFn,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { inject, Injectable } from '@angular/core';
import { AuthenticatorService } from '../service/authenticatorService';
import { UserService } from '../../api/services/user.service';
import { MessageService } from 'primeng/api';
import { AmplifyService } from '../../amplify/amplify.service';
import { firstValueFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { userProfile, userPermissions } from '../state/actions/authentication.actions';
import { selectUserRole } from '../state/selector';
import { AccessControlPermissionsService } from '../../api/services/access-control-permissions.service';
import { TenantService } from '../service/tenantService';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private readonly router = inject(Router);
  private readonly authenticatorService = inject(AuthenticatorService);
  private readonly userService = inject(UserService);
  private readonly messageService = inject(MessageService);
  private readonly amplifyAuth = inject(AmplifyService);
  private readonly store = inject(Store);
  private readonly permissionsService = inject(AccessControlPermissionsService);
  private readonly tenantService = inject(TenantService);

  constructor() {}

  canActivate: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const isAuthenticated = await this.authenticatorService.isAuthenticated();

    if (!isAuthenticated) {
      return this.router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
        queryParamsHandling: 'merge',
      });
    }

    try {
      const user = await firstValueFrom(this.userService.getOwnUser());
      if (user?.status === 'Deactivated') {
        this.messageService.add({
          severity: 'error',
          summary: 'Access denied',
          detail: 'Your account has been deactivated. Please contact your administrator.',
        });
        try {
          await this.amplifyAuth.signOut();
        } catch (err) {
          console.warn('Sign out failed', err);
        }
        return this.router.createUrlTree(['/login'], {
          queryParams: { reason: 'deactivated', ts: Date.now() },
          queryParamsHandling: 'merge',
        });
      }

      // Cognito already issued valid tokens once signIn() succeeds even when MFA setup
      // hasn't been completed yet (OPTIONAL pool config has no partial/pending auth state),
      // so a held session (e.g. via browser back navigation) can otherwise bypass the
      // mfa-setup detour entirely. Re-check on every protected navigation, not just at login.
      // MFA is a tenant-level setting enforced on non-SSO users only.
      const mfaRequiredForUser = await firstValueFrom(this.tenantService.isMfaRequiredForUser(user?.email));
      if (mfaRequiredForUser && !user?.mfaEnrolledAt) {
        return this.router.createUrlTree(['/mfa-setup']);
      }

      // Sync NgRx state with current DB role
      const previousRole = await firstValueFrom(this.store.select(selectUserRole));
      this.store.dispatch(userProfile({ userProfile: user }));

      if (user.accessControl && user.accessControl !== previousRole) {
        const perms = await firstValueFrom(
          this.permissionsService.listPermissionsByAccessControl({ accessControl: user.accessControl })
        );
        this.store.dispatch(userPermissions({ userPermissions: perms }));
      }
    } catch (err) {
      console.warn('AuthGuard user check failed', err);
    }

    return true;
  };

  canActivateChild: CanActivateChildFn = this.canActivate;
}
